import React from 'react';
import { RefreshCw } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { COMPARE_NONE, RUN_NONE, statusBadgeVariant } from './helpers';
import { ThicknessPanelController } from './useCADThicknessPanel';
import { resolveApiErrorMessage } from '../../../utils/apiErrorMessages';

interface ThicknessHistoryControlsProps {
  ctrl: ThicknessPanelController;
  uid: string;
  canAnalyze: boolean;
}

export const ThicknessHistoryControls: React.FC<ThicknessHistoryControlsProps> = ({
  ctrl,
  uid,
  canAnalyze,
}) => {
  const {
    t,
    i18n,
    analyses,
    analysisListLoaded,
    selectedAnalysisId,
    selectAnalysis,
    latestSuccessfulId,
    selectedRecord,
    selectedRecordIsStale,
    starting,
    recovering,
    startAnalysis,
    handleRecover,
    displayedAnalysis,
    comparisonBaseId,
    setComparisonBaseId,
    setComparison,
    comparison,
    comparing,
    compareSelectedRuns,
    invalidRequestedAnalysisId,
  } = ctrl;

  return (
    <>
      <p className="text-sm font-bold text-foreground">
        {t('cad.thickness.history')}
      </p>
      {analyses.length > 0 ? (
        <div className="mt-2 flex flex-col gap-1.5">
          <Label htmlFor={`${uid}-run`} className="text-xs">
            {t('cad.thickness.analysisRun')}
          </Label>
          <Select
            value={selectedAnalysisId || RUN_NONE}
            onValueChange={selectAnalysis}
          >
            <SelectTrigger id={`${uid}-run`} className="w-full">
              {/*
                A deep link to a run that does not exist leaves nothing selected.
                main's MUI version prompted for a valid run with a disabled menu
                row, which Radix cannot express — its items reject empty values
                and a sentinel row would look selectable. SelectValue's
                `placeholder` is no help either: the value is the RUN_NONE
                sentinel rather than empty, and Radix renders nothing at all when
                the current value matches no item. So the hint is rendered here
                directly whenever there is no selection.
              */}
              {selectedAnalysisId ? (
                <SelectValue />
              ) : (
                <span className="text-muted-foreground">
                  {invalidRequestedAnalysisId
                    ? t('cad.thickness.selectValidAnalysis')
                    : t('cad.thickness.analysisRun')}
                </span>
              )}
            </SelectTrigger>
            <SelectContent>
              {analyses.map((analysis) => (
                <SelectItem key={analysis.analysisId} value={analysis.analysisId}>
                  {new Date(analysis.createdAt).toLocaleString(
                    i18n.language.startsWith('en') ? 'en-US' : 'ja-JP',
                  )}{' '}
                  · {t(`cad.thickness.status.${analysis.status}`)}
                  {analysis.analysisId === latestSuccessfulId
                    ? ` · ${t('cad.thickness.latestSuccessful')}`
                    : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : analysisListLoaded && (
        <p className="mt-2 text-sm text-muted-foreground">
          {t('cad.thickness.noHistory')}
        </p>
      )}

      {selectedRecord && (
        <div className="mt-2 flex flex-row items-center gap-1.5">
          <Badge variant={statusBadgeVariant(selectedRecord.status)}>
            {t(`cad.thickness.status.${selectedRecord.status}`)}
          </Badge>
          <span className="text-xs text-muted-foreground">
            {selectedRecord.analysisId.slice(0, 8)}
          </span>
          {selectedRecord.status === 'failed' && canAnalyze && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => startAnalysis(selectedRecord.analysisId)}
              disabled={starting}
            >
              <RefreshCw />
              {t('cad.thickness.retry')}
            </Button>
          )}
          {selectedRecordIsStale && canAnalyze && (
            <Button
              size="sm"
              variant="ghost"
              onClick={handleRecover}
              disabled={recovering}
            >
              {recovering ? <Spinner size="sm" label="" /> : <RefreshCw />}
              {t('cad.thickness.recover')}
            </Button>
          )}
        </div>
      )}
      {selectedRecord?.status === 'failed' && (
        <Alert variant="destructive" className="mt-2">
          <AlertDescription>
            {resolveApiErrorMessage(t, { code: selectedRecord.errorCode }, {
              fallbackKey: 'cad.thickness.analysisFailed',
            })}
            {displayedAnalysis && (
              <span className="mt-1 block text-xs">
                {t('cad.thickness.keepLatestSuccessful')}
              </span>
            )}
          </AlertDescription>
        </Alert>
      )}
      {displayedAnalysis && analyses.filter((item) =>
        item.status === 'completed'
        && item.analysisId !== displayedAnalysis.analysisId
      ).length > 0 && (
        <div className="mt-2">
          <div className="flex flex-row items-end gap-1.5">
            <div className="flex flex-1 flex-col gap-1.5">
              <Label htmlFor={`${uid}-compare`} className="text-xs">
                {t('cad.thickness.comparison.base')}
              </Label>
              <Select
                value={comparisonBaseId || COMPARE_NONE}
                onValueChange={(value) => {
                  setComparisonBaseId(value);
                  setComparison(null);
                }}
              >
                <SelectTrigger id={`${uid}-compare`} className="w-full">
                  <SelectValue placeholder={t('cad.thickness.comparison.base')} />
                </SelectTrigger>
                <SelectContent>
                  {analyses
                    .filter((item) =>
                      item.status === 'completed'
                      && item.analysisId !== displayedAnalysis.analysisId
                    )
                    .map((item) => (
                      <SelectItem key={item.analysisId} value={item.analysisId}>
                        {new Date(item.createdAt).toLocaleString(
                          i18n.language.startsWith('en') ? 'en-US' : 'ja-JP',
                        )}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              variant="outline"
              onClick={compareSelectedRuns}
              disabled={!comparisonBaseId || comparing}
            >
              {t('cad.thickness.comparison.run')}
            </Button>
          </div>
          {comparison && (
            <Alert
              variant={
                comparison.assessment === 'improved'
                  ? 'success'
                  : comparison.assessment === 'regressed'
                    ? 'warning'
                    : 'info'
              }
              className="mt-1.5"
            >
              <AlertDescription>
                {t(`cad.thickness.comparison.assessment.${comparison.assessment}`)}
                <span className="block text-xs">
                  {t('cad.thickness.comparison.delta', {
                    thin: comparison.deltas.thin_regions || 0,
                    thick: comparison.deltas.thick_regions || 0,
                    faces: comparison.faceChanges.length,
                  })}
                </span>
              </AlertDescription>
            </Alert>
          )}
        </div>
      )}
      {(selectedRecord?.status === 'pending' || selectedRecord?.status === 'processing') && (
        <Alert variant="info" className="mt-2">
          <div className="flex flex-row gap-2">
            <Spinner size="sm" label="" className="mt-0.5 text-primary" />
            <div className="flex-1">
              {t('cad.thickness.analysisRunning')}
              <Progress
                value={selectedRecord.progressPercent}
                className="mt-1.5"
              />
              <span className="mt-0.5 block text-xs">
                {selectedRecord.progressPercent}% · {t('cad.thickness.attempt', {
                  count: selectedRecord.attemptCount,
                })}
              </span>
              {displayedAnalysis && (
                <span className="block text-xs">
                  {t('cad.thickness.keepLatestSuccessful')}
                </span>
              )}
            </div>
          </div>
        </Alert>
      )}
    </>
  );
};
